import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/styling.ts';
import { Card, CardContent } from '@/shared/components';
import { useElementHeight } from '@/shared/lib';
import {
  usePendingAccountsStore,
  type PendingAccount,
} from '@/features/account-open';
import {
  AccountAssetsList,
  AccountPendingItem,
  sortAssets,
  useAccountsStore,
  useAssetsActionButtons,
  type AccountsRestsByType,
  type AccountType,
  type MarginAccount,
  type RegularAccount,
  type AssetGroup,
  ASSETS_GROUPS,
  type AccountsRestsBaseResponseDto,
} from '@/entities/account';
import { ItemsListSkeleton } from '../ItemsListSkeleton.tsx';
import type { AccountAsset, BaseAsset } from '@/shared/types';

type Props = {
  isBalancesHidden: boolean;
  isLowBalancesHidden: boolean;
  selectedAccountType: AccountType | null;
  accountsRests: AccountsRestsByType | undefined | null;
  assetsIcons: Record<string, string> | undefined;
  isLoading: boolean;
  className?: string;
  baseAsset: BaseAsset;
  basePrecission: number;
  isVerified: boolean;
  accountsQty: number;
  openDepositModal: (props?: {
    assetGroup: AssetGroup;
    assetCode?: string;
  }) => void;
  openWithdrawModal: (props?: {
    assetGroup: AssetGroup;
    assetCode?: string;
  }) => void;
  openInternalTransferFormModal: (context?: {
    accountType: AccountType;
    accountNumber: string;
    assetCode: string;
    direction: 'from' | 'to';
  }) => void;
  openVerificationInfoModal: () => void;
  baseAccountAssets?: AccountAsset[];
};

const AssetsList = ({
  isBalancesHidden,
  isLowBalancesHidden,
  selectedAccountType,
  assetsIcons,
  accountsRests,
  isLoading: isDataLoading,
  className,
  baseAsset,
  basePrecission,
  isVerified,
  accountsQty,
  openDepositModal,
  openWithdrawModal,
  openInternalTransferFormModal,
  openVerificationInfoModal,
  baseAccountAssets,
}: Props) => {
  const { t } = useTranslation();

  const { getButtonsVisibility } = useAssetsActionButtons({
    isVerified,
    accountsQty,
  });

  const { ewallet, invest, spot, leverage, earn } = useAccountsStore();
  const { pendingAccounts } = usePendingAccountsStore();

  const checkIfAssetsEmptyAndFill = (
    accounts: (RegularAccount | PendingAccount)[]
  ) => {
    return accounts.map(account => {
      if ('timeoutSeconds' in account) {
        return account;
      }

      if (account.assets && account.assets.length === 0) {
        const foundBaseAsset = baseAccountAssets?.find(
          asset => asset.code === baseAsset
        );
        return {
          ...account,
          assets: [
            {
              code: foundBaseAsset?.code ?? baseAsset,
              name: foundBaseAsset?.name ?? baseAsset,
              precision: 6,
              group: ASSETS_GROUPS.crypto,
              available: 0,
              baseAvailable: 0,
              baseAvailable00h: 0,
              baseReserved: 0,
              baseReserved00h: 0,
              reserved: 0,
            },
          ],
        };
      }

      return account;
    });
  };

  const allAccounts: Record<
    AccountType,
    Array<PendingAccount | MarginAccount | RegularAccount>
  > = useMemo(() => {
    if (!ewallet || !invest || !spot || !leverage || !earn) {
      return {
        ewallet: [],
        invest: [],
        spot: [],
        leverage: [],
        earn: [],
      };
    }

    const investPendingAccounts: Array<PendingAccount> = [];
    const spotPendingAccounts: Array<PendingAccount> = [];
    const leveragePendingAccounts: Array<PendingAccount> = [];
    const earnPendingAccounts: Array<PendingAccount> = [];

    pendingAccounts.forEach(account => {
      if (account.accountType === 'invest') {
        investPendingAccounts.push(account);
      } else if (account.accountType === 'spot') {
        spotPendingAccounts.push(account);
      } else if (account.accountType === 'margin') {
        leveragePendingAccounts.push(account);
      } else if (account.accountType === 'earn') {
        earnPendingAccounts.push(account);
      }
    });

    return {
      ewallet: checkIfAssetsEmptyAndFill(ewallet.accounts),
      invest: [...invest.accounts, ...investPendingAccounts],
      spot: checkIfAssetsEmptyAndFill([
        ...spot.accounts,
        ...spotPendingAccounts,
      ]),
      leverage: [...leverage.accounts, ...leveragePendingAccounts],
      earn: [...earn.accounts, ...earnPendingAccounts],
    };
  }, [pendingAccounts, ewallet, invest, spot, leverage, earn]);

  const accountsTitlesByType: Record<keyof AccountsRestsByType, string> =
    useMemo(() => {
      return {
        ewallet: t('personalAccount.accountsSection.ewalletTitle'),
        invest: t('personalAccount.accountsSection.investTitle'),
        spot: t('personalAccount.accountsSection.spotTitle'),
        leverage: t('personalAccount.accountsSection.leverageTitle'),
        earn: t('personalAccount.accountsSection.earnTitle'),
      };
    }, [t]);

  const { ref: contentRef } = useElementHeight<HTMLDivElement>({
    initialMethod: 'scrollHeight',
    dynamicMethod: 'contentRect',
    trackChanges: true,
  });

  if (isDataLoading) {
    return <ItemsListSkeleton qty={4} />;
  }

  return (
    <Card
      className={cn(
        // By fact x-axis padding is 3.2, but we set half to provide correct hover area
        // Another half is inside of item itself
        'px-[1.6rem] py-[2rem] shadow-none flex flex-col tablet:flex-1 tablet:min-h-0 tablet:overflow-hidden',
        className
      )}>
      <CardContent className="p-0 tablet:overflow-y-auto tablet:min-h-0 tablet:flex-1">
        <div ref={contentRef} className="min-h-0">
          {Object.entries(allAccounts).map(([type, accounts]) => {
            if (selectedAccountType && selectedAccountType !== type) {
              return null;
            }

            return accounts.map(
              (account: PendingAccount | MarginAccount | RegularAccount) => {
                const isPending = 'remainingSeconds' in account;
                const typeName = accountsTitlesByType[type as AccountType];

                if (isPending) {
                  return (
                    <AccountPendingItem
                      key={account.accountNumber}
                      accountTypeName={typeName}
                      accountNumber={account.accountNumber}
                    />
                  );
                }

                if (!account.accNum) {
                  return null;
                }

                const sortedAssets = sortAssets(account.assets);

                const leverage =
                  type === 'leverage' || type === 'invest' || type === 'earn'
                    ? `${account?.tradingProductCode} ${account?.leverage?.value}x`
                    : undefined;

                const marginIndicators =
                  'marginIndicators' in account
                    ? account?.marginIndicators
                    : 'assets' in account &&
                        account.assets?.[0] &&
                        'baseEquity' in account.assets[0]
                      ? {
                          equity: account.assets[0].equity ?? 0,
                          baseEquity: account.assets[0].baseEquity ?? 0,
                          baseEquity00h: account.assets[0].baseEquity00h ?? 0,
                          margin: account.assets[0].margin ?? 0,
                          baseMargin: account.assets[0].baseMargin ?? 0,
                        }
                      : undefined;

                const baseLowBalance = () => {
                  if (!accountsRests) return 10;

                  if (
                    type === 'leverage' ||
                    type === 'invest' ||
                    type === 'earn'
                  ) {
                    return accountsRests[type]?.baseLowBalance ?? 10;
                  }

                  return (
                    (
                      accountsRests[
                        type as AccountType
                      ] as AccountsRestsBaseResponseDto[]
                    )?.[0]?.baseLowBalance ?? 10
                  );
                };

                return (
                  <AccountAssetsList
                    isBalancesHidden={isBalancesHidden}
                    isLowBalancesHidden={isLowBalancesHidden}
                    baseLowBalance={baseLowBalance()}
                    isVerified={isVerified}
                    openVerificationInfoModal={openVerificationInfoModal}
                    leverage={leverage}
                    key={account.accNum}
                    type={type as AccountType}
                    typeName={typeName}
                    accountNumber={account.accNum}
                    tradingAccKey={account.tradingAccKey}
                    accountAssets={sortedAssets}
                    assetsIcons={assetsIcons}
                    marginIndicators={marginIndicators}
                    baseAsset={baseAsset}
                    basePrecission={basePrecission}
                    getButtonsVisibility={getButtonsVisibility}
                    openDepositModal={openDepositModal}
                    openWithdrawModal={openWithdrawModal}
                    openInternalTransferFormModal={
                      openInternalTransferFormModal
                    }
                  />
                );
              }
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export { AssetsList };
